/**
 * N2-closeout — build the cap-exceeded modal body so it reflects the step's
 * REAL `maxSelections` instead of the old hardcoded "6 items" / "1 item".
 *
 * Behaviour:
 *  - No CMS body → generate the default sentence from `max` (pluralised).
 *  - CMS body present → substitute a `{max}` token with the real number; if
 *    no token is present, replace the legacy "<n> item(s) maximum" phrasing
 *    with the real number so existing CMS copy still reads correctly.
 *
 * `max` is the value from `getMaxSelections` (the ONE convention). A modal
 * only ever fires for a FINITE cap (unlimited steps never trip it), so an
 * `Infinity`/absent `max` falls back to a safe generic sentence.
 */
export function capModalBody(cmsBody: string | undefined, max: number | undefined): string {
  const finite = typeof max === 'number' && Number.isFinite(max) && max > 0;
  const n = finite ? max : undefined;
  const itemWord = n === 1 ? 'item' : 'items';

  if (!cmsBody) {
    const count = n ?? 'a limited number of';
    return `You can select ${count} ${itemWord} maximum. Please leave a note with your quote and we will design your box exactly the way you want it.`;
  }

  if (n === undefined) return cmsBody;

  if (cmsBody.includes('{max}')) {
    return cmsBody.replace(/\{max\}/g, String(n));
  }
  // Legacy CMS copy: "You can select 6 items maximum." → swap the number +
  // pluralisation to the real cap.
  return cmsBody.replace(/\b\d+\s+items?\s+maximum\b/i, `${n} ${itemWord} maximum`);
}
